import { useEffect, useState, useMemo } from 'react'
import { useLocation } from 'react-router-dom'
import {
  faHouseChimney,
  faBook,
  faBooks,
  faFileCertificate,
  faFileImport,
  faComments,
  faUserLock,
  faGear,
  faPenToSquare,
  faChartLine,
  faCloudUpload,
  faCodeBranch,
  IconDefinition,
  faGraduationCap,
  faFileChartColumn,
  faVideo,
} from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import { getIntegration } from 'api/integrations'
import { isMockWebinarsUiDevEnabled } from 'api/webinars/isMockWebinarsUiDevEnabled'
import { IntegrationZoom } from 'models/integrations'
import { useReaderMessage } from 'contexts/ReaderMessageContext'
import { useAccess } from 'contexts/AccessContext'
export interface IMenuItem {
  active: boolean
  icon?: IconDefinition
  /** When false, item is hidden (ABAC / feature). Sections use true when any child visible. */
  visible: boolean
  text: string
  url?: string
  badgeValue?: number
  subItems?: IMenuItem[]
}

/** Ficha participante / resultados: no son pantallas de gestión de configuración. */
export const isParticipantProfileRoute = (pathname: string) =>
  pathname.startsWith('/configuration/user-profile')

const matchesMenuItemUrl = (
  pathname: string,
  url?: string,
  includeParticipantProfileInConfiguration = false
) => {
  if (!url) return false
  if (url === '/configuration') {
    if (!pathname.startsWith('/configuration')) return false
    if (includeParticipantProfileInConfiguration) return true
    return !isParticipantProfileRoute(pathname)
  }
  return pathname.startsWith(url)
}

const matchesConfigurationSubItem = (
  pathname: string,
  subItemUrl?: string,
  includeParticipantProfileInConfiguration = false
) => {
  if (!subItemUrl) return false

  if (subItemUrl === '/configuration/users') {
    return (
      pathname.startsWith('/configuration/users') ||
      (includeParticipantProfileInConfiguration &&
        isParticipantProfileRoute(pathname))
    )
  }

  return pathname.startsWith(subItemUrl)
}

export const useMenuItems = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()
  const [menuItems, setMenuItems] = useState<IMenuItem[]>([])
  const [activeMenuItem, setActiveMenuItem] = useState(0)
  const [activeSubMenuItem, setActiveSubMenuItem] = useState(0)
  const [integrationZoom, setIntegrationZoom] = useState<IntegrationZoom>()
  const { unReadMessages } = useReaderMessage()
  const { can, isAdmin, modules, loading } = useAccess()
  const visibility = useMemo(() => {
    const full = isAdmin()
    const isCoreAdmin = full
    const canAccessConfigurationMenu = isCoreAdmin

    return {
      learningCenter: true,
      home: true,
      myCourses: true,
      library: true,
      certificates: true,
      externalCourses: true,
      messages: true,
      webinars:
        isCoreAdmin &&
        (Boolean(integrationZoom?.active) || isMockWebinarsUiDevEnabled()),
      managementSection: true,
      configuration: canAccessConfigurationMenu,
      configurationParticipants: canAccessConfigurationMenu,
      configurationAdminTabs: isCoreAdmin,
      rolesAndPermissions: isCoreAdmin,
      courseManagement:
        modules.administration ||
        can([
          'manageInternalCourses',
          'manageExternalCourses',
          'viewCourseDetails',
          'manageParticipants',
        ]),
      coursesTab: can([
        'manageInternalCourses',
        'viewCourseDetails',
        'manageParticipants',
      ]),
      certificatesTab: can('manageInternalCourses'),
      externalCoursesTab: can('manageExternalCourses'),
      learningPaths: modules.learningPaths,
      courseRequests: can('manageCourseRequests'),
      internalRequests: isCoreAdmin,
      monitoring: modules.monitoring,
      reports: isCoreAdmin,
      importCenter: isCoreAdmin,
    }
  }, [modules, can, isAdmin, integrationZoom?.active])

  useEffect(() => {
    const fetchZoom = async () => {
      try {
        const result = await getIntegration('zoom')
        if (result?.active) {
          setIntegrationZoom(result as IntegrationZoom)
        }
      } catch (error) {
        console.error('Error fetching Zoom integration:', error)
      }
    }

    fetchZoom()
  }, [])

  useEffect(() => {
    if (loading) return

    const courseSubItems = [
      {
        active: true,
        visible: visibility.coursesTab,
        text: t('commons.sidebar.menu.coursesManagement.courses'),
        url: '/administration/courses',
      },
      {
        active: true,
        visible: visibility.certificatesTab,
        text: t('commons.sidebar.menu.coursesManagement.certificates'),
        url: '/administration/courses/templates',
      },
      {
        active: true,
        visible: visibility.externalCoursesTab,
        text: t('commons.sidebar.menu.coursesManagement.externalCourses'),
        url: '/administration/courses/external',
      },
    ].filter((item) => item.visible)

    const configSubItems = [
      {
        active: true,
        visible: visibility.rolesAndPermissions,
        text: t('commons.sidebar.menu.settings.rolesAndPermissions'),
        url: '/configuration/roles-and-permissions',
      },
      {
        active: true,
        visible: visibility.configurationParticipants,
        text: t('commons.sidebar.menu.settings.participants'),
        url: '/configuration/users',
      },
      {
        active: true,
        visible: visibility.configurationAdminTabs,
        text: t('commons.sidebar.menu.settings.areasHierarchy'),
        url: '/configuration/hierarchy',
      },
      {
        active: true,
        visible: visibility.configurationAdminTabs,
        text: t('commons.sidebar.menu.settings.profiles'),
        url: '/configuration/lms-profiles',
      },
      {
        active: true,
        visible: visibility.configurationAdminTabs,
        text: t('commons.sidebar.menu.settings.translations'),
        url: '/configuration/translations',
      },
      {
        active: true,
        visible: visibility.configurationAdminTabs,
        text: t('commons.sidebar.menu.settings.integrations'),
        url: '/configuration/integrations',
      },
      {
        active: true,
        visible: visibility.configurationAdminTabs,
        text: t('commons.sidebar.menu.settings.audit'),
        url: '/configuration/audit',
      },
    ].filter((item) => item.visible)

    const showManagementHeader =
      visibility.configuration ||
      visibility.courseManagement ||
      visibility.learningPaths ||
      visibility.courseRequests ||
      visibility.internalRequests ||
      visibility.monitoring ||
      visibility.reports ||
      visibility.importCenter

    const items: IMenuItem[] = [
      {
        active: true,
        visible: true,
        text: t('commons.sidebar.menu.sections.learningCenter'),
      },
      {
        active: true,
        icon: faHouseChimney,
        visible: visibility.home,
        text: t('commons.sidebar.menu.home'),
        url: '/',
      },
      {
        active: true,
        icon: faBook,
        visible: visibility.myCourses,
        text: t('commons.sidebar.menu.myCourses'),
        url: '/my-courses',
      },
      {
        active: true,
        icon: faBooks,
        visible: visibility.library,
        text: t('commons.sidebar.menu.library'),
        url: '/library',
      },
      {
        active: true,
        icon: faFileCertificate,
        visible: visibility.certificates,
        text: t('commons.sidebar.menu.myCertificates'),
        url: '/certificates',
      },
      {
        active: true,
        icon: faFileImport,
        visible: visibility.externalCourses,
        text: t('commons.sidebar.menu.myExternalCourses'),
        url: '/external-courses',
      },
      {
        active: true,
        icon: faComments,
        visible: visibility.messages,
        text: t('commons.sidebar.menu.myMessages'),
        url: '/messages',
        badgeValue: unReadMessages,
      },
      {
        active: visibility.webinars,
        icon: faVideo,
        visible: visibility.webinars,
        text: t('commons.sidebar.menu.webinars'),
        url: '/webinars',
      },
      {
        active: true,
        visible: showManagementHeader,
        text: t('commons.sidebar.menu.sections.management'),
      },
      {
        active: true,
        icon: faGear,
        visible: visibility.configuration && configSubItems.length > 0,
        text: t('commons.sidebar.menu.configuration'),
        url: '/configuration',
        subItems: configSubItems,
      },
      {
        active: true,
        icon: faPenToSquare,
        visible: visibility.courseManagement && courseSubItems.length > 0,
        text: t('commons.sidebar.menu.courseManagement'),
        url: courseSubItems[0]?.url || '/administration/courses',
        subItems: courseSubItems,
      },
      {
        active: true,
        icon: faGraduationCap,
        visible: visibility.learningPaths,
        text: t('commons.sidebar.menu.learningPaths'),
        url: '/administration/learning-paths',
      },
      {
        active: true,
        icon: faUserLock,
        visible: visibility.courseRequests,
        text: t('commons.sidebar.menu.courseRequests', {
          defaultValue: t('commons.sidebar.menu.adminRequests'),
        }),
        url: '/admin-applications',
      },
      {
        active: visibility.internalRequests,
        icon: faCodeBranch,
        visible: visibility.internalRequests,
        text: t('commons.sidebar.menu.internalRequests'),
        url: '/requests',
      },
      {
        active: true,
        icon: faChartLine,
        visible: visibility.monitoring,
        text: t('commons.sidebar.menu.monitoring'),
        url: '/administration/monitoring',
      },
      {
        active: true,
        icon: faFileChartColumn,
        visible: visibility.reports,
        text: t('commons.sidebar.menu.reports'),
        url: '/reports',
      },
      {
        active: true,
        icon: faCloudUpload,
        visible: visibility.importCenter,
        text: t('commons.sidebar.menu.importCenterV2'),
        url: '/import-center',
      },
    ].filter((item) => item.active && item.visible)

    setMenuItems(items)

    let activeIndex = 0
    const includeParticipantProfileInConfiguration = isAdmin()
    items.forEach((item, index) => {
      if (
        matchesMenuItemUrl(
          pathname,
          item.url,
          includeParticipantProfileInConfiguration
        )
      ) {
        activeIndex = index
        let subActiveIndex = -1
        item.subItems?.forEach((subItem, subIndex) => {
          if (
            matchesConfigurationSubItem(
              pathname,
              subItem.url,
              includeParticipantProfileInConfiguration
            )
          ) {
            subActiveIndex = subIndex
          }
        })
        if (subActiveIndex >= 0) {
          setActiveSubMenuItem(subActiveIndex)
        }
      }
    })
    setActiveMenuItem(activeIndex)
  }, [loading, visibility, unReadMessages, t, pathname, isAdmin])

  // Subpestañas solo si hay más de una visible (p. ej. Cursos / Certificados / Externos).
  const activeSubItems = menuItems[activeMenuItem]?.subItems
  const showSubMenu = (activeSubItems?.length ?? 0) > 1

  return {
    activeMenuItem,
    menuItems,
    activeSubMenuItem,
    setActiveSubMenuItem,
    showSubMenu,
  }
}
